import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";
const ACCENT = "#2563EB";

const slug = s => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/** Panel rendered in a portal so it is never clipped by card overflow. */
const Panel = ({ anchorRef, open, children, testId }) => {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const measure = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (r) setRect({ top: r.bottom + 6, left: r.left, width: r.width });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, anchorRef]);

  if (!open || !rect) return null;
  return createPortal(
    <div data-testid={testId} style={{
      position: "fixed", top: rect.top, left: rect.left, width: rect.width, zIndex: 400,
      background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10,
      maxHeight: 300, overflowY: "auto", boxShadow: "0 24px 48px rgba(0,0,0,0.6)", padding: "4px 0",
    }}>
      {children}
    </div>,
    document.body,
  );
};

const fieldBase = {
  background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8,
  fontFamily: "'DM Sans', sans-serif", color: TEXT_PRIMARY,
};

export const FieldLabel = ({ children, hint }) => (
  <div style={{ marginBottom: 7 }}>
    <label style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em", display: "block" }}>
      {children}
    </label>
    {hint && <p style={{ fontSize: 11, color: TEXT_DIM, marginTop: 4 }}>{hint}</p>}
  </div>
);

/** Styled single-choice dropdown — replaces native <select> for consistent dark chrome. */
export const Dropdown = ({ testId, value, onChange, options, placeholder = "Any", formatOption }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = e => {
      if (e.target.closest && e.target.closest("[data-icp-panel]")) return;
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const label = value ? (formatOption ? formatOption(value) : value) : placeholder;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" data-testid={testId} onClick={() => setOpen(o => !o)} style={{
        ...fieldBase, width: "100%", padding: "10px 14px", fontSize: 13, cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        borderColor: open ? ACCENT : BORDER, transition: "border-color 0.15s",
      }}>
        <span style={{ color: value ? TEXT_PRIMARY : TEXT_MUTED, textAlign: "left" }}>{label}</span>
        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth={2}
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      <Panel anchorRef={ref} open={open} testId={testId ? `${testId}-panel` : undefined}>
        <div data-icp-panel="true">
          <button type="button" onClick={() => { onChange(""); setOpen(false); }} style={{
            display: "block", width: "100%", textAlign: "left", background: "none", border: "none",
            padding: "9px 14px", fontSize: 13, color: TEXT_MUTED, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
          }}>{placeholder}</button>
          {options.map(o => (
            <button key={o} type="button" data-testid={testId ? `${testId}-option-${slug(o)}` : undefined}
              onClick={() => { onChange(o); setOpen(false); }}
              onMouseEnter={e => e.currentTarget.style.background = SURFACE_2}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              style={{
                display: "flex", width: "100%", textAlign: "left", background: "none", border: "none",
                padding: "9px 14px", fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                color: o === value ? ACCENT : TEXT_PRIMARY, fontWeight: o === value ? 600 : 400,
              }}>
              {formatOption ? formatOption(o) : o}
            </button>
          ))}
        </div>
      </Panel>
    </div>
  );
};

/**
 * Searchable multi-select combobox. Accepts either a flat `options` array or
 * grouped `groups` ([{category, options}]). `allowCustom` lets the user add
 * values that aren't in the preset list. `groupSelectAll` renders an
 * "All / None" toggle on each group header (used by the country picker).
 */
export const MultiSelect = ({
  testId, value = [], onChange, options, groups, placeholder = "Search…",
  allowCustom = false, groupSelectAll = false, maxVisibleChips = 40, chipColor = ACCENT,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const normGroups = useMemo(
    () => groups || [{ category: null, options: options || [] }],
    [groups, options],
  );

  useEffect(() => {
    if (!open) return;
    const onDown = e => {
      if (e.target.closest && e.target.closest("[data-icp-panel]")) return;
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(""); }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => normGroups
    .map(g => ({ ...g, options: g.options.filter(o => !q || o.toLowerCase().includes(q)) }))
    .filter(g => g.options.length > 0), [normGroups, q]);

  const flatFiltered = filtered.flatMap(g => g.options);
  const allKnown = useMemo(() => normGroups.flatMap(g => g.options), [normGroups]);
  const exactExists = allKnown.some(o => o.toLowerCase() === q);
  const canAddCustom = allowCustom && q.length > 0 && !exactExists && flatFiltered.length === 0 && !value.some(v => v.toLowerCase() === q);

  const toggle = v => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  const addCustom = () => {
    const v = query.trim();
    if (!v) return;
    if (!value.some(x => x.toLowerCase() === v.toLowerCase())) onChange([...value, v]);
    setQuery("");
  };

  const toggleGroup = g => {
    const all = g.options.every(o => value.includes(o));
    onChange(all ? value.filter(v => !g.options.includes(v)) : [...new Set([...value, ...g.options])]);
  };

  const onKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (canAddCustom) addCustom();
      else if (flatFiltered.length > 0) { toggle(flatFiltered[0]); setQuery(""); }
    } else if (e.key === "Backspace" && !query && value.length > 0) {
      onChange(value.slice(0, -1));
    } else if (e.key === "Escape") {
      setOpen(false); setQuery("");
    }
  };

  return (
    <div ref={ref} data-testid={testId} style={{ position: "relative" }}>
      <div onClick={() => setOpen(true)} style={{
        ...fieldBase, padding: "7px 10px", display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center",
        cursor: "text", borderColor: open ? ACCENT : BORDER, transition: "border-color 0.15s", minHeight: 42,
      }}>
        {value.slice(0, maxVisibleChips).map(v => (
          <span key={v} data-testid={testId ? `${testId}-chip-${slug(v)}` : undefined} style={{
            fontSize: 12, padding: "3px 6px 3px 10px", borderRadius: 16, display: "inline-flex",
            alignItems: "center", gap: 4, background: `${chipColor}18`,
            border: `1px solid ${chipColor}44`, color: chipColor, maxWidth: 260,
          }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
            <button type="button" aria-label={`Remove ${v}`}
              data-testid={testId ? `${testId}-remove-${slug(v)}` : undefined}
              onClick={e => { e.stopPropagation(); onChange(value.filter(x => x !== v)); }}
              style={{ background: "none", border: "none", color: chipColor, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 2px" }}>
              ×
            </button>
          </span>
        ))}
        {value.length > maxVisibleChips && (
          <span style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "'DM Mono', monospace" }}>
            +{value.length - maxVisibleChips} more
          </span>
        )}
        <input
          data-testid={testId ? `${testId}-input` : undefined}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={value.length === 0 ? placeholder : ""}
          style={{
            flex: 1, minWidth: 120, background: "transparent", border: "none", outline: "none",
            fontSize: 13, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", padding: "4px 2px",
          }}
        />
      </div>

      <Panel anchorRef={ref} open={open} testId={testId ? `${testId}-panel` : undefined}>
        <div data-icp-panel="true">
          {canAddCustom && (
            <button type="button" data-testid={testId ? `${testId}-add-custom` : undefined} onClick={addCustom} style={{
              display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
              background: "none", border: "none", padding: "9px 14px", fontSize: 13, color: ACCENT,
              cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
            }}>
              + Add “{query.trim()}”
            </button>
          )}
          {filtered.length === 0 && !canAddCustom && (
            <div style={{ padding: "14px", fontSize: 12, color: TEXT_MUTED }}>No matches</div>
          )}
          {filtered.map(g => (
            <div key={g.category || "_flat"}>
              {g.category && (
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 14px 4px", fontSize: 9, color: TEXT_DIM,
                  fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase",
                }}>
                  <span>{g.category}</span>
                  {groupSelectAll && (
                    <button type="button" data-testid={testId ? `${testId}-group-all-${slug(g.category)}` : undefined}
                      onClick={() => toggleGroup(g)}
                      style={{ background: "none", border: "none", color: ACCENT, cursor: "pointer", fontSize: 9, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em" }}>
                      {g.options.every(o => value.includes(o)) ? "CLEAR" : "SELECT ALL"}
                    </button>
                  )}
                </div>
              )}
              {g.options.map(o => {
                const selected = value.includes(o);
                return (
                  <button key={o} type="button"
                    data-testid={testId ? `${testId}-option-${slug(o)}` : undefined}
                    onClick={() => toggle(o)}
                    onMouseEnter={e => e.currentTarget.style.background = SURFACE_2}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                      background: "none", border: "none", padding: "8px 14px", fontSize: 13,
                      cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                      color: selected ? ACCENT : TEXT_PRIMARY, fontWeight: selected ? 600 : 400,
                    }}>
                    <span style={{
                      width: 14, height: 14, borderRadius: 4, flexShrink: 0,
                      border: `1px solid ${selected ? ACCENT : BORDER}`,
                      background: selected ? ACCENT : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {selected && <svg width={9} height={9} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}><path d="M20 6L9 17l-5-5" /></svg>}
                    </span>
                    {o}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </Panel>
      {value.length > 0 && (
        <div data-testid={testId ? `${testId}-count` : undefined} style={{ marginTop: 6, fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em" }}>
          {value.length} SELECTED
        </div>
      )}
    </div>
  );
};
